import { create } from "zustand";
import { devtools } from "zustand/middleware";
import useLoaderStore from "./useLoaderStore";
import { useToastStore } from "./toastStore";

import { fetchWithHeaders } from "../api/fetchWithHeaders";


type ScenarioPayload = {
	items: any[];
	total_items: number;
	page: number;
	page_size: number;
	scenario_name: string;
};

export type ScenarioCpvItem = {
	scenario_name: string;
	total_target_cpv: number;
	currency: string;
	is_recommended?: boolean;
};

export type ScenarioCpvSummary = {
	negotiation_id: number;
	scenarios: ScenarioCpvItem[];
};

type AllocationChange = {
	tender_scenario_id: number;
	allocation_pct: number;
};

interface PlanningStore {
	listOfScenarios: [];
	scenariosTab: [];
    scenarioTable: ScenarioPayload | null;
	scenarioCpvSummary: ScenarioCpvSummary | null;
	getListOfScenarios: (negotiationId: any) => Promise<any>;
	createScenarios: (negotiationId: any) => Promise<any>;
    fetchScenarioTableData: (negotiationId: any, scenarioName: any, page: any, pageSize: any) => Promise<any>;
	getScenarioCpvSummary: (negotiationId: number) => Promise<ScenarioCpvSummary | null>;
	updateVol: (tender_scenario_id: any, payload: any) => Promise<any>;
	updateBulkAllocations: (negotiationId: string | undefined, scenarioName: string | undefined, changes: AllocationChange[], isRecommended: boolean) => Promise<any>;
	deleteScenario: (negotiationId: number, scenarioName: string) => Promise<any>;
	resetData: () => void
}

export const usePlanningStore = create<PlanningStore>()(
	devtools((set) => ({
		listOfScenarios: [],
		scenariosTab: [],
        scenarioTable: [],
		scenarioCpvSummary: null,

		getListOfScenarios: async ( negotiationId: number) => {
			const { showLoader } = useLoaderStore.getState();
			const { error } = useToastStore.getState();
			const url = import.meta.env.VITE_API_BASE_URL;
			showLoader(true);
			const params = new URLSearchParams({
				negotiation_id: String(negotiationId),
			})
			try {
				const resp = await fetchWithHeaders(
					url + `api/v1/tenders/get_scenario_tabs?${params.toString()}`,
					{
						method: "GET",
					}
				);
				const data = await resp.json();


				set({ listOfScenarios: data });
				return data;

			} catch (err) {
				showLoader(false);
				error("Error", `Failed to fetch reports: ${err}`);
			} finally {
				showLoader(false);
			}
		},

		createScenarios: async (negotiationId: number) => {
			const { showLoader } = useLoaderStore.getState();
			const { error } = useToastStore.getState();
			const url = import.meta.env.VITE_API_BASE_URL;
			showLoader(true);
			try {
				const resp = await fetchWithHeaders(
					url + `api/v1/tenders/negotiation-packages/${negotiationId}/scenarios`,
					{
						method: "POST",
					}
				);
				const data = await resp.json();

				if (!data?.success) {
					error("Error", `Failed to create scenario: ${data?.message}`);
					return null;
				}

				set({ scenariosTab: data });
				return data;

			} catch (err) {
				showLoader(false);
				console.log(err);
			} finally {
				showLoader(false);
			}
		},
		fetchScenarioTableData: async (
			negotiationId: number,
			scenarioName: string,
			page: number,
			pageSize: number
		) => {
			// /tender/scenario_table?negotiation_id=1&scenario_name=As%20Is%20Scenario&page=1&page_size=10
			const { showLoader } = useLoaderStore.getState();
			const { error } = useToastStore.getState();

			const url = import.meta.env.VITE_API_BASE_URL;
			const params = new URLSearchParams({
				negotiation_id: String(negotiationId),
				scenario_name: scenarioName,
				page: String(page),
				page_size: String(pageSize),
			});

			showLoader(true);
			try {
				const resp = await fetchWithHeaders(
					`${url}api/v1/tenders/scenario_table?${params.toString()}`,
					{
						method: "GET",
					}
				);
				const data = await resp.json();
				set({ scenarioTable: data });
				return data;

			} catch (err) {
				showLoader(false);
				error("Error", `Failed to fetch data: ${err}`);
			} finally {
				showLoader(false);
			}
		},
		getScenarioCpvSummary: async (negotiationId: number) => {
			const { showLoader } = useLoaderStore.getState();
			const { error } = useToastStore.getState();
			const url = import.meta.env.VITE_API_BASE_URL;
			const params = new URLSearchParams({
				negotiation_id: String(negotiationId),
			});

			showLoader(true);
			try {
				const resp = await fetchWithHeaders(
					`${url}api/v1/tenders/scenario_cpv_summary?${params.toString()}`,
					{
						method: "GET",
					}
				);
				const data = await resp.json();
				set({ scenarioCpvSummary: data });
				return data;

			} catch (err) {
				showLoader(false);
				error("Error", `Failed to fetch scenario CPV summary: ${err}`);
				return null;
			} finally {
				showLoader(false);
			}
		},
		updateVol: async (
			tender_scenario_id: number,
			payload: any
		) => {
			const { showLoader } = useLoaderStore.getState();
			const { success, error } = useToastStore.getState();

			const url = import.meta.env.VITE_API_BASE_URL;
			const params = new URLSearchParams({
				tender_scenario_id: String(tender_scenario_id),
			})
			showLoader(true);
			try {
				const resp = await fetchWithHeaders(
					url + `api/v1/tenders/update_scenario?${params.toString()}`,
					{
						method: "PUT",
						body: JSON.stringify(payload),
					}
				);
				const data = await resp.json();

				// Check if the response indicates an error
				if (data?.detail?.success === false || !resp.ok) {
					error("Error", data?.detail?.message || data?.message || "Failed to update allocation");
					return null;
				}

				success("Success", data?.message || "Allocation updated successfully");
				return data;

			} catch (err) {
				showLoader(false);
				error("Error", `Failed to fetch data: ${err}`);
			} finally {
				showLoader(false);
			}
		},
		updateBulkAllocations: async (
			negotiationId: string | undefined,
			scenarioName: string | undefined,
			changes: AllocationChange[],
			isRecommended: boolean
		) => {
			const { showLoader } = useLoaderStore.getState();
			const { success, error } = useToastStore.getState();
			const url = import.meta.env.VITE_API_BASE_URL;

			if (!negotiationId) {
				error("Error", "Negotiation ID is required");
				return null;
			}

			showLoader(true);
			try {
				// Call the bulk update endpoint
				const payload = {
					updates: changes.map(change => ({
						tender_scenario_id: change.tender_scenario_id,
						volumn_allocation: change.allocation_pct,
					})),
					is_recommended: isRecommended,
					negotiation_id: Number(negotiationId),
					scenario_name: scenarioName,
				};

				const resp = await fetchWithHeaders(
					url + `api/v1/tenders/bulk_update_scenario`,
					{
						method: "PUT",
						body: JSON.stringify(payload),
					}
				);

				const data = await resp.json();

				showLoader(false);

				// Check if the response indicates an error
				if (data?.detail?.success === false || !resp.ok) {
					error("Error", data?.detail?.message || data?.message || "Failed to update allocations");
					throw new Error(data?.detail?.message || data?.message || "Failed to update allocations");
				}

				success("Success", data?.message || `Successfully updated ${changes.length} allocation(s)`);
				return data;

			} catch (err) {
				showLoader(false);
				// Error already shown above, just rethrow
				throw err;
			}
		},
		deleteScenario: async (negotiationId: number, scenarioName: string) => {
			const { showLoader } = useLoaderStore.getState();
			const { success, error } = useToastStore.getState();
			const url = import.meta.env.VITE_API_BASE_URL;
			const params = new URLSearchParams({
				negotiation_id: String(negotiationId),
				scenario_name: scenarioName,
			});

			showLoader(true);
			try {
				const resp = await fetchWithHeaders(
					`${url}api/v1/tenders/delete_scenario_by_name?${params.toString()}`,
					{
						method: "DELETE",
					}
				);
				const data = await resp.json();

				if (data?.detail?.success === false || !resp.ok) {
					error("Error", data?.detail?.message || data?.message || "Failed to delete scenario");
					return null;
				}

				success("Success", data?.message || "Scenario deleted successfully");
				return data;

			} catch (err) {
				showLoader(false);
				error("Error", `Failed to delete scenario: ${err}`);
				return null;
			} finally {
				showLoader(false);
			}
		},
		resetData: () => {
			set({
				listOfScenarios: [],
				scenariosTab: [],
				scenarioTable:  null,
				scenarioCpvSummary: null,
			});
		}
	}))
);
